import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../contexts/AuthContext'
import { rolePermissionService } from '../services/rolePermissionService'
import { RolePermission } from '../types/rolePermission'
import { UserRole, RoleCategory } from '../types/user'
import { RolePermissionCard } from '../components/RolePermissionCard'
import { RolePermissionEditor } from '../components/RolePermissionEditor'
import './SettingsRolePermissions.css'

export function SettingsRolePermissions() {
  const queryClient = useQueryClient()
  const { hasPermission } = useAuth()
  const [editingRole, setEditingRole] = useState<UserRole | null>(null)

  const { data: rolePermissions, isLoading, error } = useQuery({
    queryKey: ['role-permissions'],
    queryFn: () => rolePermissionService.getAll(),
  })

  const { data: templates } = useQuery({
    queryKey: ['permission-templates'],
    queryFn: () => rolePermissionService.getTemplates(),
  })

  const updateMutation = useMutation({
    mutationFn: ({ role, permissions }: { role: UserRole; permissions: string[] }) =>
      rolePermissionService.update(role, permissions),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['role-permissions'] })
      setEditingRole(null)
    },
  })

  // Check if user has permission to edit role permissions
  const canEdit = hasPermission('role-permissions:write') || hasPermission('*')

  const applyTemplateMutation = useMutation({
    mutationFn: ({ role, templateName }: { role: UserRole; templateName: string }) =>
      rolePermissionService.applyTemplate(role, templateName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['role-permissions'] })
    },
  })

  const handleSave = async (permissions: string[]) => {
    if (!editingRole) return
    await updateMutation.mutateAsync({ role: editingRole, permissions })
  }

  const handleApplyTemplate = async (role: UserRole, templateName: string) => {
    await applyTemplateMutation.mutateAsync({ role, templateName })
  }

  // Group roles by category
  const rolesByCategory = useMemo(() => {
    if (!rolePermissions) return { Clinical: [], Administrative: [], System: [] }

    const grouped: Record<RoleCategory, RolePermission[]> = {
      Clinical: [],
      Administrative: [],
      System: [],
    }

    rolePermissions.forEach((rp) => {
      grouped[rp.roleCategory].push(rp)
    })

    return grouped
  }, [rolePermissions])

  const editingRolePermission = editingRole
    ? rolePermissions?.find((rp) => rp.role === editingRole)
    : null

  // Check if user has permission to view role permissions
  if (!hasPermission('role-permissions:read') && !hasPermission('*')) {
    return (
      <div className="settings-content" role="main">
        <div className="error" role="alert">
          <h2>Access Denied</h2>
          <p>You don't have permission to view role permissions.</p>
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="settings-content" role="main">
        <div className="loading" role="status" aria-live="polite">
          Loading role permissions...
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="settings-content" role="main">
        <div className="error" role="alert" aria-live="assertive">
          Error loading role permissions: {error instanceof Error ? error.message : 'Unknown error'}
        </div>
      </div>
    )
  }

  // Show editor if editing
  if (editingRole && editingRolePermission) {
    return (
      <div className="settings-content" role="main">
        <div className="settings-header">
          <h1>Edit Role Permissions</h1>
          <button
            className="btn-secondary"
            onClick={() => setEditingRole(null)}
            aria-label="Back to role permissions list"
          >
            Back to Role Permissions
          </button>
        </div>
        {(updateMutation.isError || applyTemplateMutation.isError) && (
          <div className="error-message" role="alert">
            Failed to save permissions. Please try again.
          </div>
        )}
        <RolePermissionEditor
          role={editingRole}
          initialPermissions={editingRolePermission.permissions}
          onSave={handleSave}
          onCancel={() => setEditingRole(null)}
          isLoading={updateMutation.isPending}
        />
      </div>
    )
  }

  // Show list view
  return (
    <div className="settings-content role-permissions-page" role="main">
      <div className="settings-header">
        <div>
          <h1>Role Permissions</h1>
          <p className="settings-subtitle">
            Manage permissions for each role in the system. Permissions control what features users
            with each role can access.
          </p>
        </div>
      </div>

      {/* Clinical Roles */}
      {rolesByCategory.Clinical.length > 0 && (
        <section className="role-category-section">
          <h2 className="category-heading">Clinical Roles</h2>
          <div className="role-cards-grid">
              {rolesByCategory.Clinical.map((rolePermission) => (
              <RolePermissionCard
                key={rolePermission.role}
                rolePermission={rolePermission}
                onEdit={canEdit ? setEditingRole : () => {}}
                onApplyTemplate={canEdit ? handleApplyTemplate : undefined}
                templates={templates?.map((t) => ({ id: t.id, name: t.name }))}
                showTemplateActions={canEdit}
              />
            ))}
          </div>
        </section>
      )}

      {/* Administrative Roles */}
      {rolesByCategory.Administrative.length > 0 && (
        <section className="role-category-section">
          <h2 className="category-heading">Administrative Roles</h2>
          <div className="role-cards-grid">
            {rolesByCategory.Administrative.map((rolePermission) => (
              <RolePermissionCard
                key={rolePermission.role}
                rolePermission={rolePermission}
                onEdit={canEdit ? setEditingRole : () => {}}
                onApplyTemplate={canEdit ? handleApplyTemplate : undefined}
                templates={templates?.map((t) => ({ id: t.id, name: t.name }))}
                showTemplateActions={canEdit}
              />
            ))}
          </div>
        </section>
      )}

      {/* System Roles */}
      {rolesByCategory.System.length > 0 && (
        <section className="role-category-section">
          <h2 className="category-heading">System Roles</h2>
          <div className="role-cards-grid">
            {rolesByCategory.System.map((rolePermission) => (
              <RolePermissionCard
                key={rolePermission.role}
                rolePermission={rolePermission}
                onEdit={canEdit ? setEditingRole : () => {}}
                onApplyTemplate={canEdit ? handleApplyTemplate : undefined}
                templates={templates?.map((t) => ({ id: t.id, name: t.name }))}
                showTemplateActions={canEdit}
              />
            ))}
          </div>
        </section>
      )}

      {/* Information Section */}
      <section className="settings-info-section">
        <h2>Understanding Role Permissions</h2>
        <div className="info-content">
          <div className="info-subsection">
            <h3>What are Role Permissions?</h3>
            <p>
              Role permissions define what users with each role can access and do in the system. 
              Permissions follow the pattern <code>resource:action</code> (e.g., <code>patients:read</code>, 
              <code>appointments:write</code>). The permissions assigned to a role define the scope of 
              practice for all users with that role.
            </p>
          </div>

          <div className="info-subsection">
            <h3>Permission Format</h3>
            <p>Permissions use a hierarchical format:</p>
            <ul className="info-list">
              <li>
                <code>resource:action</code> - Basic permission (e.g., <code>patients:read</code>, 
                <code>appointments:write</code>)
              </li>
              <li>
                <code>resource:action:field</code> - Field-level permission (e.g., 
                <code>patients:read:medications</code>, <code>patients:read:conditions</code>)
              </li>
              <li>
                <code>resource:action:full-access</code> - Full access permission (e.g., 
                <code>patients:read:full-access</code> - required for confidential/highly-confidential data)
              </li>
              <li>
                <code>*</code> - Wildcard permission (grants all permissions - typically only for admin roles)
              </li>
            </ul>
          </div>

          <div className="info-subsection">
            <h3>Field-Level Permissions</h3>
            <p>
              Field-level permissions control access to specific sensitive patient data fields:
            </p>
            <ul className="info-list">
              <li><code>patients:read:medications</code> - Access to patient medications</li>
              <li><code>patients:read:conditions</code> - Access to patient conditions</li>
              <li><code>patients:read:vital-signs</code> - Access to vital signs</li>
              <li><code>patients:read:lab-results</code> - Access to lab results</li>
              <li><code>patients:read:allergies</code> - Access to allergy information</li>
              <li><code>patients:read:family-history</code> - Access to family history</li>
              <li><code>patients:read:sensitive-documents</code> - Access to sensitive documents</li>
              <li><code>patients:read:full-access</code> - Full access (required for confidential/highly-confidential data)</li>
            </ul>
            <p>
              <strong>Important:</strong> The basic <code>patients:read</code> permission only grants access to 
              non-sensitive patient information (name, DOB, contact info). To access sensitive fields like medications 
              or conditions, users need explicit field-level permissions.
            </p>
          </div>

          <div className="info-subsection">
            <h3>How to Configure Role Permissions</h3>
            <ol className="info-list">
              <li>
                <strong>Select a Role:</strong> Click "Edit" on any role card to modify its permissions
              </li>
              <li>
                <strong>Use Templates:</strong> Click "Apply Template" to quickly apply a predefined permission set 
                (e.g., "Doctor - Clinical Access", "Receptionist - Administrative")
              </li>
              <li>
                <strong>Customize Permissions:</strong> Check or uncheck individual permissions to customize what each 
                role can access
              </li>
              <li>
                <strong>Add Field-Level Permissions:</strong> For roles that need access to sensitive data, add 
                field-level permissions (e.g., <code>patients:read:medications</code>)
              </li>
              <li>
                <strong>Save Changes:</strong> Click "Save" to apply the permissions to the role
              </li>
            </ol>
          </div>

          <div className="info-subsection">
            <h3>Role Permissions and Confidentiality</h3>
            <p>
              Role permissions work together with confidentiality levels to control data access:
            </p>
            <ul className="info-list">
              <li>
                <strong>Public/Restricted Data:</strong> Requires field-level permission (e.g., 
                <code>patients:read:medications</code>)
              </li>
              <li>
                <strong>Confidential/Highly-Confidential Data:</strong> Requires <code>patients:read:full-access</code> 
                permission, even if the user has the field-level permission
              </li>
              <li>
                <strong>Emergency Override:</strong> Users with <code>patients:override:emergency</code> permission 
                can request temporary access to restricted data
              </li>
            </ul>
          </div>

          <div className="info-subsection">
            <h3>Example Scenarios</h3>
            <div className="scenario-list">
              <div className="scenario-item">
                <strong>Receptionist Role:</strong>
                <ul>
                  <li>Permissions: <code>patients:read</code>, <code>patients:write</code>, 
                  <code>appointments:read</code>, <code>appointments:write</code></li>
                  <li>Result: Can view basic patient info and manage appointments, but cannot see medications, 
                  conditions, or other sensitive data</li>
                </ul>
              </div>
              <div className="scenario-item" style={{ marginTop: '1rem' }}>
                <strong>Doctor Role:</strong>
                <ul>
                  <li>Permissions: <code>patients:read</code>, <code>patients:read:medications</code>, 
                  <code>patients:read:conditions</code>, <code>patients:read:vital-signs</code>, 
                  <code>patients:read:lab-results</code>, <code>patients:read:full-access</code></li>
                  <li>Result: Can view all patient data, including sensitive fields and confidential information</li>
                </ul>
              </div>
              <div className="scenario-item" style={{ marginTop: '1rem' }}>
                <strong>Therapist Role:</strong>
                <ul>
                  <li>Permissions: <code>patients:read</code>, <code>patients:read:conditions</code>, 
                  <code>patients:read:medications</code> (for mental health context)</li>
                  <li>Result: Can view mental health conditions and psychiatric medications, but may not have 
                  access to other sensitive data unless <code>patients:read:full-access</code> is granted</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="info-subsection">
            <h3>Key Points</h3>
            <ul className="info-list">
              <li>Role permissions define the <strong>scope of practice</strong> for all users with that role</li>
              <li>All users with the same role share the same permissions (scope of practice)</li>
              <li>Field-level permissions are required to access sensitive patient data</li>
              <li><code>patients:read:full-access</code> is required for confidential/highly-confidential data</li>
              <li>Use permission templates to quickly set up standard role configurations</li>
              <li>Changes to role permissions affect all users with that role immediately</li>
              <li>For different scopes within the same role type, create separate roles (e.g., 'doctor-general', 'doctor-mental-health')</li>
            </ul>
          </div>
        </div>
      </section>
    </div>
  )
}
